
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import Sheet2Tab from '../components/tabs/Sheet2Tab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace('http', 'ws') + '/api/ws/trading';

const LiveTrading = () => {
    const [activeTab, setActiveTab] = useState('execute');

    // ============ EXECUTE TRADE STATE ============
    const [status, setStatus] = useState("Stopped");
    const [logs, setLogs] = useState([]);
    const logsEndRef = useRef(null);

    const addLog = (msg, type = 'info') => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prev => [...prev.slice(-49), { time: timestamp, msg, type }]);
    };

    useEffect(() => {
        logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [logs]);
    const [metrics, setMetrics] = useState({
        mpw: 0, mProfit: 0, mLoss: 0, maxCapital: 0, greenCapital: 0,
        gRoi: 0, mPercent: 0, mr: 0, totalLoss: 0, taxes: 0, total_legs: 0,
        nifty: 0, banknifty: 0, sensex: 0
    });
    const [gridData, setGridData] = useState([]);
    const [config, setConfig] = useState({
        all_green: "",
        lt_vt: "VT",
        limit_market: "MARKET",
        max_profit: "",
        max_loss: "",
        mr_plus: "",
        mr_minus: ""
    });
    const socketRef = useRef(null);

    // ============ EXECUTE TRADE LOGIC ============
    const handleStart = async () => {
        addLog("Sending command: START...", "action");
        try {
            await axios.post(`${BACKEND_URL}/api/trading/start`, {}, { withCredentials: true });
            toast.success("Engine Initialized");
            addLog("Command Sent: START", "success");
        } catch (e) { 
            toast.error("Start Failed"); 
            addLog("Command Failed: START", "error");
        }
    };

    const handleStop = async () => {
        addLog("Sending command: STOP...", "action");
        try {
            await axios.post(`${BACKEND_URL}/api/trading/stop`, {}, { withCredentials: true });
            toast.error("Engine Terminated");
            addLog("Command Sent: STOP", "success");
        } catch (e) { 
            toast.error("Stop Failed"); 
            addLog("Command Failed: STOP", "error");
        }
    };

    useEffect(() => {
        // Only connect if active tab is execute to save resources or keep it always running?
        // Original request says "Sheet 2 should be in new tab and backend dedicatedly for sheet 2"
        // So we keep them independent.
        if (activeTab !== 'execute') return;

        const connect = () => {
            const ws = new WebSocket(WS_URL);
            socketRef.current = ws;
            
            ws.onopen = () => addLog("WebSocket Connected", "success");

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.metrics) setMetrics(prev => ({ ...prev, ...data.metrics }));
                    if (data.orders) setGridData(data.orders);
                    if (data.status) {
                        setStatus(prev => {
                            if (prev !== data.status) {
                                addLog(`Status Changed: ${prev} -> ${data.status}`, "warning");
                            }
                            return data.status;
                        });
                    }
                } catch (e) { }
            };
            ws.onclose = () => {
                addLog("WebSocket Disconnected. Reconnecting...", "error");
                setTimeout(connect, 3000);
            };
        };
        connect();
        return () => socketRef.current?.close();
    }, [activeTab]);

    const handleAction = async (action) => {
        if (status === "Stopped" && action !== "RESET_ALL") {
            toast.warning("Engine Idle - Start Required");
            addLog(`Blocked Action: ${action} (Engine Stopped)`, "warning");
            return;
        }
        addLog(`Sending Action: ${action}...`, "action");
        try {
            await axios.post(`${BACKEND_URL}/api/trading/action`, { action }, { withCredentials: true });
            toast.success(`${action} Executed`);
            addLog(`Action Logic Triggered: ${action}`, "success");
        } catch (e) { 
            toast.error("Action Failed"); 
            addLog(`Action Failed: ${action}`, "error");
        }
    };

    const updateConfig = async (newConfig) => {
        addLog("Syncing Config to Backend...", "action");
        try {
            const data = { ...newConfig };
            const changedKeys = Object.keys(data).filter(k => data[k] !== "").join(", ");

            ['max_profit', 'max_loss', 'mr_plus', 'mr_minus'].forEach(f => {
                data[f] = data[f] === "" ? null : parseFloat(data[f]);
            });
            await axios.post(`${BACKEND_URL}/api/trading/config`, data, { withCredentials: true });
            toast.success("Config Synced");
            addLog(`Config Synced. Updated: [${changedKeys || "All"}]`, "success");
        } catch (e) { 
            toast.error("Sync Failed"); 
            addLog("Config Sync Failed", "error");
        }
    };

    const handleConfigKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            updateConfig(config);
        }
    };

    return (
        <div className="flex flex-col h-screen w-full bg-[#fdfdfd] text-[13px] font-sans overflow-hidden select-none">
            {/* Tab Navigation */}
            <div className="bg-white border-b px-4 py-1 flex gap-4 shadow-sm shrink-0">
                <Button
                    variant={activeTab === 'execute' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('execute')}
                    className={activeTab === 'execute' ? 'font-bold' : ''}
                >
                    Execute Trade
                </Button>
                <Button
                    variant={activeTab === 'sheet2' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('sheet2')}
                    className={activeTab === 'sheet2' ? 'font-bold' : ''}
                >
                    Sheet 2
                </Button>
            </div>

            {activeTab === 'sheet2' ? (
                <div className="flex-1 overflow-auto">
                    <Sheet2Tab />
                </div>
            ) : (
                <div className="flex flex-row flex-1 overflow-hidden">
                    {/* Optimized Sidebar */}
                    <div className="w-28 flex-shrink-0 flex flex-col gap-3 p-3 border-r border-slate-200 bg-slate-50 shadow-[inset_-1px_0_0_rgba(0,0,0,0.05)]">
                        <Button onClick={() => handleAction("RESET_ALL")} variant="outline" className="h-10 border-red-200 text-red-500 hover:bg-red-500 hover:text-white font-bold text-[10px] uppercase shadow-sm">Reset All</Button>

                        <div className="mt-4 flex flex-col gap-2">
                            {status === "Stopped" ? (
                                <Button onClick={handleStart} className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md transition-all">START</Button>
                            ) : (
                                <Button onClick={() => handleAction("CONTINUE")} variant="outline" className="h-9 border-emerald-500 text-emerald-600 hover:bg-emerald-500 hover:text-white font-bold text-[10px]">CONTINUE</Button>
                            )}
                            <Button onClick={() => handleAction("PAUSE")} variant="outline" className="h-9 border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white font-bold text-[10px]">PAUSE</Button>
                            <Button onClick={handleStop} variant="outline" className="h-9 border-rose-500 text-rose-500 hover:bg-rose-500 hover:text-white font-bold text-[10px]">EXIT</Button>
                        </div>

                        <div className="mt-auto pb-4">
                            <Button onClick={() => handleAction("RE_ORDER")} variant="secondary" className="w-full h-8 text-[9px] font-bold bg-slate-200 border border-slate-300">RE-ORDER</Button>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 flex flex-col min-w-0 bg-white">

                        {/* Header Grid - Fixed Section */}
                        <div className="p-3 flex-shrink-0">
                            <div className="grid grid-cols-24 border-t border-l border-slate-300 shadow-sm rounded overflow-hidden">

                                {/* Row 0: Overlays */}
                                <div className="col-span-8 h-8 border-r border-b border-slate-300 bg-white flex items-center px-3 font-semibold text-slate-600">
                                    <span className="text-[11px] uppercase mr-2 tracking-wide text-orange-600">All Green</span>
                                    <input name="all_green" value={config.all_green} onChange={(e) => setConfig(p => ({ ...p, all_green: e.target.value }))} onKeyDown={handleConfigKeyDown} className="w-16 h-5 border border-slate-200 rounded text-center font-mono text-slate-800 focus:ring-1 focus:ring-orange-200 outline-none" />
                                </div>
                                <div className="col-span-4 border-r border-b border-slate-300 flex items-center justify-center gap-2 bg-slate-50">
                                    <button onClick={() => handleAction("EXIT_CE")} className="px-2 py-0.5 border border-rose-300 text-[9px] font-bold text-rose-600 rounded bg-white hover:bg-rose-500 hover:text-white transition-colors uppercase whitespace-nowrap">Exit CE</button>
                                    <button onClick={() => handleAction("EXIT_PE")} className="px-2 py-0.5 border border-rose-300 text-[9px] font-bold text-rose-600 rounded bg-white hover:bg-rose-500 hover:text-white transition-colors uppercase whitespace-nowrap">Exit PE</button>
                                </div>
                                <div className="col-span-9 border-r border-b border-slate-300 bg-white"></div>
                                <div className="col-span-3 border-r border-b border-slate-300 flex items-center justify-center p-1 bg-slate-50">
                                    <div className="flex w-full bg-white rounded border border-slate-200 p-0.5 shadow-inner">
                                        <button onClick={() => updateConfig({ ...config, limit_market: 'LIMIT' })} className={`flex-1 h-5 text-[8px] font-bold rounded ${config.limit_market === 'LIMIT' ? 'bg-rose-500 text-white' : 'text-slate-400 hover:bg-slate-50'}`}>LIMIT</button>
                                        <button onClick={() => updateConfig({ ...config, limit_market: 'MARKET' })} className={`flex-1 h-5 text-[8px] font-bold rounded ${config.limit_market === 'MARKET' ? 'bg-rose-500 text-white' : 'text-slate-400 hover:bg-slate-50'}`}>MARKET</button>
                                    </div>
                                </div>

                                {/* Row 1: Status & Indexes */}
                                <div className={`col-span-6 h-11 flex items-center justify-center font-bold text-white shadow-inner border-r border-b border-slate-300 ${status === 'Running' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : 'bg-gradient-to-r from-rose-500 to-rose-600'}`}>
                                    {status.toUpperCase()}
                                </div>
                                <div className="col-span-2 bg-slate-800 text-white text-[11px] font-bold flex items-center justify-center border-r border-b border-slate-300 uppercase tracking-tighter">Nifty</div>
                                <div className="col-span-2 bg-slate-800 text-white text-[10px] font-bold flex items-center justify-center border-r border-b border-slate-300 uppercase tracking-tighter text-center">Bank<br />Nifty</div>
                                <div className="col-span-2 bg-slate-800 text-white text-[10px] font-bold flex items-center justify-center border-r border-b border-slate-300 uppercase tracking-tighter">Sensex</div>
                                <div className="col-span-7 bg-gradient-to-r from-emerald-500 to-emerald-400 text-white font-bold flex items-center justify-center border-r border-b border-slate-300 text-[11px] uppercase tracking-wide">Ready • Normal</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-emerald-500 text-white font-bold text-[9px] flex items-center justify-center uppercase">G ROI</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-emerald-500 text-white font-bold text-[9px] flex items-center justify-center uppercase">Legs</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-emerald-500 text-white font-bold text-[9px] flex items-center justify-center uppercase">Taxes</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-emerald-500 text-white font-bold text-[9px] flex items-center justify-center uppercase">MR+</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-emerald-500 text-white font-bold text-[9px] flex items-center justify-center uppercase">MR-</div>

                                {/* Row 2: Labels */}
                                <div className="col-span-3 h-11 border-r border-b border-slate-300 bg-emerald-600 text-white font-bold text-[11px] flex items-center justify-center uppercase">Max Profit</div>
                                <div className="col-span-3 border-r border-b border-slate-300 bg-white p-0">
                                    <input name="max_profit" value={config.max_profit} onChange={(e) => setConfig(p => ({ ...p, max_profit: e.target.value }))} onKeyDown={handleConfigKeyDown} className="w-full h-full text-center font-bold text-emerald-600 outline-none bg-emerald-50/30 text-[14px]" placeholder="" />
                                </div>
                                <div className="col-span-6 border-r border-b border-slate-300 bg-slate-100 font-bold text-[10px] flex items-center justify-center uppercase text-slate-400">SPOT INDICATORS</div>
                                <div className="col-span-3 border-r border-b border-slate-300 bg-cyan-100 font-bold text-[10px] flex items-center justify-center uppercase text-cyan-700">Max Capital</div>
                                <div className="col-span-4 border-r border-b border-slate-300 bg-cyan-100 font-bold text-[10px] flex items-center justify-center uppercase text-cyan-700">Green Capital</div>
                                <div className="col-span-5 border-r border-b border-slate-300 bg-white"></div>

                                {/* Row 3: Live Data */}
                                <div className="col-span-3 h-11 border-r border-b border-slate-300 bg-amber-400 text-white font-bold text-[11px] flex items-center justify-center uppercase">Max Loss</div>
                                <div className="col-span-3 border-r border-b border-slate-300 bg-white p-0">
                                    <input name="max_loss" value={config.max_loss} onChange={(e) => setConfig(p => ({ ...p, max_loss: e.target.value }))} onKeyDown={handleConfigKeyDown} className="w-full h-full text-center font-bold text-rose-600 outline-none bg-rose-50/30 text-[14px]" placeholder="" />
                                </div>
                                <div className="col-span-2 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[12px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.nifty ? metrics.nifty.toFixed(1) : ''}</div>
                                <div className="col-span-2 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[12px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.banknifty ? metrics.banknifty.toFixed(1) : ''}</div>
                                <div className="col-span-2 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[12px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.sensex ? metrics.sensex.toFixed(1) : ''}</div>
                                <div className="col-span-3 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[11px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.maxCapital ? '₹' + metrics.maxCapital.toFixed(0) : ''}</div>
                                <div className="col-span-4 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[11px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.greenCapital ? '₹' + metrics.greenCapital.toFixed(0) : ''}</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[10px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.gRoi ? metrics.gRoi.toFixed(2) + '%' : ''}</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[11px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.total_legs || ''}</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-cyan-400 font-bold text-[9px] flex items-center justify-center text-slate-900 border-t border-cyan-500 font-mono">{metrics.taxes ? '₹' + metrics.taxes.toFixed(0) : ''}</div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-cyan-100 p-0">
                                    <input name="mr_plus" value={config.mr_plus} onChange={(e) => setConfig(p => ({ ...p, mr_plus: e.target.value }))} onKeyDown={handleConfigKeyDown} className="w-full h-full text-center outline-none bg-transparent font-bold text-[12px]" />
                                </div>
                                <div className="col-span-1 border-r border-b border-slate-300 bg-cyan-100 p-0">
                                    <input name="mr_minus" value={config.mr_minus} onChange={(e) => setConfig(p => ({ ...p, mr_minus: e.target.value }))} onKeyDown={handleConfigKeyDown} className="w-full h-full text-center outline-none bg-transparent font-bold text-[12px]" />
                                </div>

                                {/* Row 4: Master MTM Widget */}
                                <div className="col-span-3 h-14 border-r border-b border-slate-300 bg-orange-500 text-white font-black text-[13px] flex items-center justify-center shadow-inner">MTM</div>
                                <div className={`col-span-11 border-r border-b border-slate-300 bg-white font-black text-2xl flex items-center justify-center ${metrics.mpw >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                    ₹ {(metrics.mpw || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </div>
                                <div className={`col-span-10 h-14 border-r border-b border-slate-300 font-black text-xl flex items-center justify-center ${metrics.mPercent >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                                    {(metrics.mPercent || 0).toFixed(2)}%
                                </div>
                            </div>
                            
                            {/* DEBUG CONSOLE */}
                            <div className="mt-2 h-24 bg-slate-900 rounded border border-slate-700 flex flex-col overflow-hidden text-[10px] font-mono shadow-lg">
                                <div className="bg-slate-800 px-2 py-1 text-slate-400 text-[9px] font-bold uppercase tracking-wider flex justify-between">
                                    <span>Engine Activity Log</span>
                                    <button onClick={() => setLogs([])} className="hover:text-white">CLEAR</button>
                                </div>
                                <div className="flex-1 overflow-y-auto p-2 space-y-1">
                                    {logs.length === 0 && <div className="text-slate-600 italic text-center p-4">Waiting for events...</div>}
                                    {logs.map((log, i) => (
                                        <div key={i} className="flex gap-2">
                                            <span className="text-slate-500 flex-shrink-0">[{log.time}]</span>
                                            <span className={`${
                                                log.type === 'error' ? 'text-rose-400' : 
                                                log.type === 'success' ? 'text-emerald-400' : 
                                                log.type === 'warning' ? 'text-amber-400' : 
                                                log.type === 'action' ? 'text-blue-400' : 'text-slate-300'
                                            }`}>
                                                {log.type === 'action' ? '> ' : ''}{log.msg}
                                            </span>
                                        </div>
                                    ))}
                                    <div ref={logsEndRef} />
                                </div>
                            </div>
                        </div>

                        {/* Data Table - Reverted to High-Contrast Excel Style */}
                        <div className="flex-1 overflow-auto px-1 pb-2">
                            <table className="w-full border-collapse text-center bg-white border-2 border-gray-800 shadow-sm">
                                <thead className="sticky top-0 z-30">
                                    <tr className="bg-[#00FFFF] h-10 border-b-2 border-gray-900 text-[12px] font-black uppercase">
                                        <th className="border border-gray-500 px-3 text-left min-w-[150px]">SYMBOL & STRIKE</th>
                                        <th className="border border-gray-500">HIGH</th>
                                        <th className="border border-gray-500">LOW</th>
                                        <th className="border border-gray-500">SL TYPE</th>
                                        <th className="border border-gray-500 px-2">SL POSITION</th>
                                        <th className="border border-gray-500">TARGET</th>
                                        <th className="border border-gray-500">EXEC PRICE</th>
                                        <th className="border border-gray-500 text-blue-900">LIVE LTP</th>
                                        <th className="border border-gray-500 text-yellow-900">MTM</th>
                                        <th className="border border-gray-500">CAPITAL</th>
                                        <th className="border border-gray-500">STATUS</th>
                                        <th className="border border-gray-500">B/S</th>
                                        <th className="border border-gray-500 text-[9px] leading-tight">ENTRY<br />EXIT</th>
                                        <th className="border border-gray-500 text-[10px] min-w-[130px]">COMPOUND KEY</th>
                                    </tr>
                                </thead>
                                <tbody className="text-[14px] font-bold">
                                    {gridData.filter(row => row['SYMBOL']).length === 0 ? (
                                        <tr>
                                            <td colSpan="14" className="h-64 bg-gray-50 text-gray-300 uppercase tracking-widest text-lg font-black italic">
                                                <div className="flex flex-col items-center gap-2">
                                                    <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                                                    Waiting for data feed...
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        gridData.filter(row => row['SYMBOL']).map((row, idx) => (
                                            <tr key={idx} className="bg-[#FFCCFF] border-b border-gray-400 hover:bg-white transition-colors h-9">
                                                <td className="border border-gray-400 text-left px-2 font-black text-black">{row['SYMBOL']}</td>
                                                <td className="border border-gray-400 font-mono text-[12px]">{row['HIGH']}</td>
                                                <td className="border border-gray-400 font-mono text-[12px]">{row['LOW']}</td>
                                                <td className="border border-gray-400 text-[11px]">{row['SL_Type']}</td>
                                                <td className="border border-gray-400 font-mono text-[12px]">{row['SL position']}</td>
                                                <td className="border border-gray-400 font-mono text-[12px]">{row['Target']}</td>
                                                <td className="border border-gray-400 font-mono text-[12px]">{row['Executed Price']}</td>
                                                <td className="border border-gray-400 font-black text-blue-900 bg-white/40">{row['Live LTP']}</td>
                                                <td className={`border border-gray-400 font-black ${parseFloat(row['MTM']) >= 0 ? 'text-green-800' : 'text-red-800'}`}>{row['MTM']}</td>
                                                <td className="border border-gray-400 text-gray-700">{row['CAPITAL'] ? '₹' + row['CAPITAL'] : ''}</td>
                                                <td className={`border border-gray-400 font-black text-[11px] uppercase ${row['Status'] === 'EXIT' ? 'bg-blue-600 text-white' : row['Status'] === 'Running' ? 'bg-emerald-600 text-white' : ''}`}>{row['Status']}</td>
                                                <td className={`border border-gray-400 font-black ${row['Buy/Sell'] === 'B' ? 'text-blue-800' : 'text-red-800'}`}>{row['Buy/Sell']}</td>
                                                <td className="border border-gray-400 text-[11px]">{row['ENTRY EXIT']}</td>
                                                <td className="border border-gray-400 text-[9px] text-gray-400 truncate max-w-[150px] px-2" title={row['SN|TP|SY|L|TF|CN|AIO|SID|OID|CG']}>{row['SN|TP|SY|L|TF|CN|AIO|SID|OID|CG']}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .grid-cols-24 { grid-template-columns: repeat(24, minmax(0, 1fr)); }
            `}</style>
        </div>
    );
};

export default LiveTrading;
